import React, { memo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useFeature } from '@growthbook/growthbook-react';
import { Icon } from '@modulor/react';
import Local from '../../Local/Local';
import BonusNavigationItem from './BonusNavigationItem';
import { useBonusesDataSelector } from '../../../hooks/API/useQueryBonuses';
import {
  wageringBonusSelector,
  collectingBonusSelector,
  freeSpinBonusSelector,
  collectingCampaignIdSelector,
} from '../../../hooks/API/selectors/bonuses';
import { useAnalyticsScreenName } from '../../../hooks/useAnalyticsScreenName';
import { useIsFavouriteGamesEnabled } from '../../../hooks/useIsFavouriteGamesEnabled';
import { isLoggedInSelector } from '../../../redux/reducers/app/params';
import {
  getRootRoute,
  getFavoritesRoute,
  getBonusGamesWageringRoute,
  getBonusGamesCollectingRoute,
  getHallOfFameRoute,
  getGameDetailRoute,
} from '../../../utils/routes';
import { getDataTestAttrs } from '../../../utils/testing-locators';
import {
  COLLECTING,
  PENDING,
  FREE_SPINS_BONUS_MECHANIC,
} from '../../../constants/bonuses';
import { NAMESPACES } from '../../../constants/namespaces';
import { FEATURES } from '../../../constants/featureToggling';
import { EVENT_ACTION } from '../../../constants/ga';
import { HALL_OF_FAME_HISTORY } from '../../../constants/routeTemplates';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREA_DETAILS,
  ANALYTICS_SCREEN_AREAS,
} from '../../../constants/analytics';
import { NAVIGATION_MAIN } from '../../../constants/dataTestIds';
import { MainNavigationItemWithAnalytics } from './MainNavigationItem';
import styles from './MainNavigation.module.css';
import TournamentNavigationItem from './TournamentNavigationItem';
import { useTournamentLobbyAvailable } from '../../../hooks/useTournamentLobbyRedirect';

const MainNavigation = ({ className }) => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const screenName = useAnalyticsScreenName();

  const isEnabledFavoriteGames = useIsFavouriteGamesEnabled();
  const isTournamentLobbyAvailable = useTournamentLobbyAvailable();

  const isHallOfFameEnabled = useFeature(FEATURES.ENABLE_HALL_OF_FAME).on;

  const wageringBonus = useBonusesDataSelector(wageringBonusSelector);
  const collectingBonus = useBonusesDataSelector(collectingBonusSelector);
  const freeSpinBonus = useBonusesDataSelector(freeSpinBonusSelector);
  const { campaignId } = useBonusesDataSelector(collectingCampaignIdSelector);

  const shouldDisplayFreeSpinsButton =
    (freeSpinBonus?.status === PENDING &&
      freeSpinBonus?.mechanicType === FREE_SPINS_BONUS_MECHANIC) ||
    freeSpinBonus?.status === COLLECTING;

  const isWageringGamesAvailable = Boolean(wageringBonus);
  const isCollectingGamesAvailable = Boolean(collectingBonus);

  let positionIndex = 0;
  const getPositionIndex = () => positionIndex++;

  return (
    <ul
      className={classNames(styles.list, className)}
      {...getDataTestAttrs(NAVIGATION_MAIN)}
    >
      <li className={styles.item}>
        <MainNavigationItemWithAnalytics
          to={getRootRoute()}
          icon={<Icon name="home_outlined" />}
          dataLayerEventData={{
            eventAction: EVENT_ACTION.NAV_MAIN,
            section_index: 0,
          }}
          analyticsData={{
            schema: ANALYTICS_SCHEMA_URLS.NAVIGATION_CLICK,
            screen_name: screenName,
            screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
            section_index: 0,
            screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.MAIN_PAGE,
            position_index: getPositionIndex(),
          }}
        >
          <Local namespace={NAMESPACES.PAGES}>Main</Local>
        </MainNavigationItemWithAnalytics>
      </li>

      <BonusNavigationItem
        screenName={screenName}
        getPositionIndex={getPositionIndex}
      />

      {isTournamentLobbyAvailable && <TournamentNavigationItem />}

      {isLoggedIn && isEnabledFavoriteGames && (
        <li className={styles.item}>
          <MainNavigationItemWithAnalytics
            to={getFavoritesRoute()}
            icon={<Icon name="star_filled" />}
            dataLayerEventData={{
              eventAction: EVENT_ACTION.NAV_FAVORITES,
              section_index: 0,
            }}
            analyticsData={{
              schema: ANALYTICS_SCHEMA_URLS.NAVIGATION_CLICK,
              screen_name: screenName,
              screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
              section_index: 0,
              position_index: getPositionIndex(),
              screen_area_detail:
                ANALYTICS_SCREEN_AREA_DETAILS.FAVORITE_PAGE_CLICK,
            }}
          >
            <Local namespace={NAMESPACES.PAGES}>Favorites</Local>
          </MainNavigationItemWithAnalytics>
        </li>
      )}

      {isLoggedIn && isCollectingGamesAvailable && (
        <li className={styles.item}>
          <MainNavigationItemWithAnalytics
            to={getBonusGamesCollectingRoute(campaignId)}
            icon={<Icon name="cashback" className={styles['cashback-icon']} />}
            dataLayerEventData={{
              eventAction: EVENT_ACTION.NAV_BONUS_GAMES_COLLECTING,
              section_index: 0,
            }}
            analyticsData={{
              schema: ANALYTICS_SCHEMA_URLS.NAVIGATION_CLICK,
              screen_name: screenName,
              screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
              section_index: 0,
              position_index: getPositionIndex(),
              screen_area_detail:
                ANALYTICS_SCREEN_AREA_DETAILS.COLLECTING_GAMES_PAGE_CLICK,
            }}
          >
            <Local namespace={NAMESPACES.PAGES}>Games for collecting</Local>
          </MainNavigationItemWithAnalytics>
        </li>
      )}

      {isLoggedIn && isWageringGamesAvailable && (
        <li className={styles.item}>
          <MainNavigationItemWithAnalytics
            to={getBonusGamesWageringRoute()}
            icon={<Icon name="wager" />}
            dataLayerEventData={{
              eventAction: EVENT_ACTION.NAV_BONUS_GAMES_WAGERING,
              section_index: 0,
            }}
            analyticsData={{
              schema: ANALYTICS_SCHEMA_URLS.NAVIGATION_CLICK,
              screen_name: screenName,
              screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
              section_index: 0,
              position_index: getPositionIndex(),
              screen_area_detail:
                ANALYTICS_SCREEN_AREA_DETAILS.WAGERING_GAMES_PAGE_CLICK,
            }}
          >
            <Local namespace={NAMESPACES.PAGES}>Games for wagering</Local>
          </MainNavigationItemWithAnalytics>
        </li>
      )}

      {isLoggedIn && shouldDisplayFreeSpinsButton && (
        <li className={styles.item}>
          <MainNavigationItemWithAnalytics
            to={getGameDetailRoute(freeSpinBonus?.lobbyGameId)}
            icon={<Icon name="free_spins" />}
            dataLayerEventData={{
              eventAction: EVENT_ACTION.NAV_BONUS_GAMES_FREE_SPINS,
              game_id: freeSpinBonus?.lobbyGameId,
              section_index: 0,
            }}
            analyticsData={{
              schema: ANALYTICS_SCHEMA_URLS.NAVIGATION_CLICK,
              screen_name: screenName,
              screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
              section_index: 0,
              position_index: getPositionIndex(),
              screen_area_detail:
                ANALYTICS_SCREEN_AREA_DETAILS.FREE_SPINS_PAGE_CLICK,
              game_id: freeSpinBonus?.lobbyGameId,
            }}
          >
            <Local namespace={NAMESPACES.PAGES}>Free spins</Local>
          </MainNavigationItemWithAnalytics>
        </li>
      )}

      {isHallOfFameEnabled && (
        <li className={styles.item}>
          <MainNavigationItemWithAnalytics
            to={getHallOfFameRoute()}
            activeOnRoutes={[HALL_OF_FAME_HISTORY]}
            icon={<Icon name="hall_of_fame_outlined" />}
            dataLayerEventData={{
              eventAction: EVENT_ACTION.NAV_HALL_OF_FAME,
              section_index: 0,
            }}
            analyticsData={{
              schema: ANALYTICS_SCHEMA_URLS.NAVIGATION_CLICK,
              screen_name: screenName,
              screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
              section_index: 0,
              position_index: getPositionIndex(),
              screen_area_detail:
                ANALYTICS_SCREEN_AREA_DETAILS.HALL_OF_FAME_PAGE_CLICK,
            }}
          >
            <Local namespace={NAMESPACES.PAGES}>Hall of fame</Local>
          </MainNavigationItemWithAnalytics>
        </li>
      )}
    </ul>
  );
};

export default memo(MainNavigation);
